import { Suspense, lazy, type ReactElement } from "react";
import {
    BrowserRouter as Router,
    Navigate,
    Routes,
    Route,
} from "react-router-dom";
import toast, { Toaster, ToastBar } from "react-hot-toast";

import ScrollToTop from "./ui/ScrollToTop";
import { routes } from "./lib/routes";
import PrivateRoute from "./PrivateRoute";
import Loader from "./ui/Loader";
import RouteErrorBoundary from "./ui/RouteErrorBoundary";

/**
 * Route-level code splitting.
 *
 * Every screen past the landing page is a dynamic import, so a first-time
 * visitor downloads the marketing site and nothing else: not the dashboard,
 * not the map SDK, not the charting library, not the whole of Chakra. The
 * previous build shipped all of it in one 2.7 MB chunk that every visitor
 * fetched before anything rendered.
 *
 * Home stays eager. It is what the majority of visitors are asking for, and
 * splitting it only adds a round trip before the page they came for.
 */
import Home from "./landing-page/pages/Home";
import { MapProvider } from "./app/Dashboard/context/Map";

// The signed-in shell, lazily: it mounts the Chakra provider, and a visitor who
// never signs in should never download it.
const Auth = lazy(() => import("./Auth"));

// ── Marketing pages ─────────────────────────────────────────────────────────
const Company = lazy(() => import("./landing-page/pages/Company"));
const Faq = lazy(() => import("./landing-page/pages/Faq"));
const TermAndConditions = lazy(
    () => import("./landing-page/pages/TermAndConditions")
);
const PrivacyPolicy = lazy(() => import("./landing-page/pages/PrivacyPolicy"));
const NotFound = lazy(() => import("./landing-page/pages/NotFound"));
const Drive = lazy(() => import("./landing-page/pages/Drive"));
const Ride = lazy(() => import("./landing-page/pages/Ride"));
const Support = lazy(() => import("./landing-page/pages/Support"));
const Safety = lazy(() => import("./landing-page/pages/Safety"));

// The public shared-trip page. Lazy like the rest: a visitor who never opens a
// shared link should not download it.
const SharedTripPage = lazy(() => import("./app/trip/SharedTrip"));

// ── Authentication ──────────────────────────────────────────────────────────
const VerifyEmail = lazy(
    () => import("./app/Authentication/verifyEmail/VerifyEmail")
);
const VerifyPhone = lazy(
    () => import("./app/Authentication/verifyPhone/VerifyPhone")
);
const ForgotPassword = lazy(
    () => import("./app/Authentication/login/forgetPassword/ForgotPassword")
);
const ResetPasword = lazy(
    () => import("./app/Authentication/login/resetPassword/ResetPasword")
);
const Login = lazy(() => import("./app/Authentication/login/Login"));
const SignUp = lazy(() => import("./app/Authentication/signUp/SignUp"));

// ── Dashboard, shared ───────────────────────────────────────────────────────
const GetStarted = lazy(
    () => import("./app/Dashboard/components/GetStarted/GetStarted")
);
const Notifications = lazy(
    () => import("./app/Dashboard/components/notifications/Notifications")
);
const PaymentFailed = lazy(
    () => import("./app/Dashboard/components/PaymentFailed")
);
const PaymentSuccess = lazy(
    () => import("./app/Dashboard/components/PaymentSuccess")
);

// ── Rider ───────────────────────────────────────────────────────────────────
const RiderDashboardContainer = lazy(
    () =>
        import("./app/Dashboard/roles/rider/components/RiderDashboardContainer")
);
const RiderProfile = lazy(
    () => import("./app/Dashboard/roles/rider/RiderProfileSettings/RiderProfile")
);
const OverviewDashboard = lazy(
    () => import("./app/Dashboard/roles/rider/overview/OverviewDashboard")
);
const BookingAndRideHistory = lazy(
    () =>
        import(
            "./app/Dashboard/roles/rider/BookingAndRideHistory/BookingAndRideHistory"
        )
);
const BookedRideDetails = lazy(
    () =>
        import(
            "./app/Dashboard/roles/rider/BookingAndRideHistory/BookedRideDetails"
        )
);

// ── Driver ──────────────────────────────────────────────────────────────────
const DriverDashboardContainer = lazy(
    () =>
        import(
            "./app/Dashboard/roles/driver/components/DriverDashboardContainer"
        )
);
const DriverSetUp = lazy(
    () => import("./app/Dashboard/roles/driver/components/DriverSetUp")
);
const DriverOverviewDashboard = lazy(
    () => import("./app/Dashboard/roles/driver/overview/DriverOverviewDashboard")
);
const BankDetails = lazy(
    () => import("./app/Dashboard/roles/driver/components/BankDetails")
);
const DriverRideDetails = lazy(
    () =>
        import(
            "./app/Dashboard/roles/driver/my-rides/shared-rides-tabs/DriverRideDetails"
        )
);
const UploadVehicleRegistration = lazy(
    () =>
        import(
            "./app/Dashboard/roles/driver/components/CreateVehicle/UploadVehicleRegistration"
        )
);
const UploadVehicleInspection = lazy(
    () =>
        import(
            "./app/Dashboard/roles/driver/components/CreateVehicle/UploadVehicleInspection"
        )
);
const UploadVehicle = lazy(
    () =>
        import(
            "./app/Dashboard/roles/driver/components/CreateVehicle/UploadVehicle"
        )
);
const MyRides = lazy(() => import("./app/Dashboard/roles/driver/my-rides/MyRides"));
const MyRidesDetails = lazy(
    () => import("./app/Dashboard/roles/driver/my-rides/MyRidesDetails")
);
const DriverProfile = lazy(
    () =>
        import(
            "./app/Dashboard/roles/driver/DriverProfileSettings/DriverProfile"
        )
);
const DriverApplicatinStatus = lazy(
    () =>
        import("./app/Dashboard/roles/driver/components/DriverApplicatinStatus")
);
const AddPhoto = lazy(
    () => import("./app/Dashboard/roles/driver/components/AddPhoto")
);
const DriversLicense = lazy(
    () => import("./app/Dashboard/roles/driver/components/DriversLicense")
);
const CreateVehicle = lazy(
    () =>
        import(
            "./app/Dashboard/roles/driver/components/CreateVehicle/CreateVehicle"
        )
);

/**
 * The panels inside the two profile screens.
 *
 * Declared here with every other route rather than in a `<Routes>` nested
 * inside the profile screen, which is where they used to live. A descendant
 * `<Routes>` is matched against the splat the parent route captured, so the
 * sidenav beside it sat in a route context whose path grew with every
 * navigation: `to="earning"` resolved against
 * `/dashboard/driver/profile/earning` and produced
 * `/dashboard/driver/profile/earning/earning`, which matched none of the
 * descendant routes and rendered an empty panel. A second click made it
 * `.../earning/earning/security`, and so on without limit.
 *
 * As real child routes the sidenav's own match stays `.../profile` whatever is
 * showing beside it, so a relative link resolves to the same URL every time.
 */
const DriverProfileSettingsPanel = lazy(
    () =>
        import(
            "./app/Dashboard/roles/driver/DriverProfileSettings/DriverProfileLayout/main/settings/DriverProfileSettings"
        )
);
const DriverVehiclePanel = lazy(
    () =>
        import(
            "./app/Dashboard/roles/driver/DriverProfileSettings/DriverProfileLayout/main/vehicle/Vehicle"
        )
);
const DriverEarningPanel = lazy(
    () =>
        import(
            "./app/Dashboard/roles/driver/DriverProfileSettings/DriverProfileLayout/main/earning/Earning"
        )
);
const DriverSecurityPanel = lazy(
    () =>
        import(
            "./app/Dashboard/roles/driver/DriverProfileSettings/DriverProfileLayout/main/security/Security"
        )
);
const DriverSupportPanel = lazy(
    () =>
        import(
            "./app/Dashboard/roles/driver/DriverProfileSettings/DriverProfileLayout/main/support/Support"
        )
);
const RiderProfileSettingsPanel = lazy(
    () =>
        import(
            "./app/Dashboard/roles/rider/RiderProfileSettings/RiderProfileLayout/main/settings/ProfileSettings"
        )
);
const RiderSecurityPanel = lazy(
    () =>
        import(
            "./app/Dashboard/roles/rider/RiderProfileSettings/RiderProfileLayout/main/security/Security"
        )
);
const RiderSupportPanel = lazy(
    () =>
        import(
            "./app/Dashboard/roles/rider/RiderProfileSettings/RiderProfileLayout/main/support/Support"
        )
);
const UploadVehicleInsurancePolicy = lazy(
    () =>
        import(
            "./app/Dashboard/roles/driver/components/CreateVehicle/UploadVehicleInsurancePolicy"
        )
);

/**
 * "Speak to customer support", reached from inside either support panel.
 *
 * One component for both personas: a support conversation belongs to the
 * account, not to the dashboard it was opened from, and the server resumes the
 * same thread whichever of the two addresses the person arrives at. It reads
 * the persona out of the URL only to know which support panel to offer as the
 * way back.
 *
 * Declared as a child of each `profile` layout route, beside the panel it is
 * opened from, so it is deep-linkable and so a reload lands back on it. Both
 * branches end in a `path="*"` catch-all, and a two-segment static path
 * outranks a splat in React Router's matcher, so `support/chat` is reached and
 * not redirected away.
 */
const SupportChatPanel = lazy(() => import("./app/support/SupportChat"));

const MajorRoutes = () => {
    const RIDERS_ROUTES = [
        {
            path: "overview",
            element: (
                <MapProvider>
                    <OverviewDashboard />
                </MapProvider>
            ),
        },
        { path: "ride-history", element: <BookingAndRideHistory /> },
        { path: "booking-details/:id", element: <BookedRideDetails /> },
        { path: "loader", element: <RiderProfile /> },
    ];

    const DRIVERS_ROUTES = [
        { path: "application-status", element: <DriverApplicatinStatus /> },
        { path: "set-up", element: <DriverSetUp /> },
        { path: "set-up/upload-profile-picture", element: <AddPhoto /> },
        { path: "set-up/upload-drivers-license", element: <DriversLicense /> },
        { path: "my-rides", element: <MyRides /> },
        { path: "my-rides/:id", element: <MyRidesDetails /> },
        { path: "driver-details/:id", element: <DriverRideDetails /> },
        { path: "set-up/create-vehicle-setup", element: <CreateVehicle /> },
        {
            path: "set-up/create-vehicle-setup/upload-vehicle-insurance-policy",
            element: <UploadVehicleInsurancePolicy />,
        },
        {
            path: "set-up/create-vehicle-setup/upload-vehicle-registration",
            element: <UploadVehicleRegistration />,
        },
        {
            path: "set-up/create-vehicle-setup/upload-vehicle-inspection",
            element: <UploadVehicleInspection />,
        },
        {
            path: "set-up/create-vehicle-setup/upload-vehicle",
            element: <UploadVehicle />,
        },
        {
            path: "set-up/bank-details",
            element: <BankDetails />,
        },
        {
            path: "overview",
            element: (
                <MapProvider>
                    <DriverOverviewDashboard />
                </MapProvider>
            ),
        },
    ];

    return (
        <>
            <Router>
                {/* Outside Suspense on purpose: a dynamic import that rejects
                    is re-thrown by Suspense, so a boundary nested inside it
                    would never see the one error it exists to handle. */}
                <RouteErrorBoundary>
                <Suspense fallback={<Loader />}>
                    <ScrollToTop>
                        {/* Exactly one Toaster in the application.
                            react-hot-toast keeps its toasts in a module-level
                            store and every mounted Toaster renders all of them,
                            so a second one on the sign-up form drew each toast
                            twice and left a copy on screen that the first one's
                            dismissal did not clear. That is the "stuck" toast.

                            Four seconds rather than ten: a confirmation nobody
                            has to read should be gone before it is in the way,
                            and ten seconds of a banner pinned over the top of
                            the page reads as broken rather than as slow. */}
                        <Toaster
                            position="top-center"
                            reverseOrder={true}
                            containerStyle={{
                                top: 100,
                            }}
                            toastOptions={{
                                duration: 4000,
                                error: { duration: 6000 },
                            }}
                        >
                            {(t) => (
                                <ToastBar toast={t}>
                                    {({ icon, message }) => (
                                        <>
                                            {t.type !== "loading" && (
                                                <button
                                                    onClick={() =>
                                                        toast.dismiss(t.id)
                                                    }
                                                    style={{
                                                        width: "50px",
                                                        background: "white",
                                                        border: "none",
                                                    }}
                                                >
                                                    {icon}
                                                </button>
                                            )}
                                            {message}
                                        </>
                                    )}
                                </ToastBar>
                            )}
                        </Toaster>

                        <Routes>
                            <Route
                                element={
                                    <PrivateRoute />
                                }
                            >
                                <Route path="/dashboard/*" element={<Auth />}>
                                    <Route
                                        path="notifications"
                                        element={<Notifications />}
                                    />
                                    <Route
                                        path="rider/*"
                                        element={<RiderDashboardContainer />}
                                    >
                                        {RIDERS_ROUTES.map(
                                            (
                                                route: {
                                                    path: string;
                                                    element: ReactElement;
                                                },
                                                id
                                            ) => (
                                                <Route
                                                    key={id}
                                                    path={route.path}
                                                    element={route.element}
                                                />
                                            )
                                        )}
                                        {/* A layout route: RiderProfile draws
                                            the sidenav and an Outlet, and the
                                            panel beside it is a child. See the
                                            panel imports above for why this is
                                            not a nested <Routes>. */}
                                        <Route
                                            path="profile"
                                            element={<RiderProfile />}
                                        >
                                            <Route
                                                index
                                                element={
                                                    <RiderProfileSettingsPanel />
                                                }
                                            />
                                            <Route
                                                path="security"
                                                element={<RiderSecurityPanel />}
                                            />
                                            <Route
                                                path="support"
                                                element={<RiderSupportPanel />}
                                            />
                                            <Route
                                                path="support/chat"
                                                element={<SupportChatPanel />}
                                            />
                                            {/* Anything else under the profile
                                                goes back to its root rather
                                                than showing an empty panel.
                                                Self-healing for the URLs the
                                                old relative links produced,
                                                which are in people's history.

                                                Absolute on purpose: "." and
                                                ".." resolve against the splat
                                                this route just captured, so a
                                                relative target here redirects
                                                to the address it is trying to
                                                leave. */}
                                            <Route
                                                path="*"
                                                element={
                                                    <Navigate
                                                        to="/dashboard/rider/profile"
                                                        replace
                                                    />
                                                }
                                            />
                                        </Route>
                                    </Route>
                                    <Route
                                        path="driver/*"
                                        element={<DriverDashboardContainer />}
                                    >
                                        {DRIVERS_ROUTES.map(
                                            (
                                                route: {
                                                    path: string;
                                                    element: ReactElement;
                                                },
                                                id
                                            ) => (
                                                <Route
                                                    key={id}
                                                    path={route.path}
                                                    element={route.element}
                                                />
                                            )
                                        )}
                                        <Route
                                            path="profile"
                                            element={<DriverProfile />}
                                        >
                                            <Route
                                                index
                                                element={
                                                    <DriverProfileSettingsPanel />
                                                }
                                            />
                                            <Route
                                                path="vehicle"
                                                element={<DriverVehiclePanel />}
                                            />
                                            <Route
                                                path="earning"
                                                element={<DriverEarningPanel />}
                                            />
                                            <Route
                                                path="security"
                                                element={<DriverSecurityPanel />}
                                            />
                                            <Route
                                                path="support"
                                                element={<DriverSupportPanel />}
                                            />
                                            <Route
                                                path="support/chat"
                                                element={<SupportChatPanel />}
                                            />
                                            {/* As above: absolute, because a
                                                relative target resolves against
                                                the splat and goes nowhere. */}
                                            <Route
                                                path="*"
                                                element={
                                                    <Navigate
                                                        to="/dashboard/driver/profile"
                                                        replace
                                                    />
                                                }
                                            />
                                        </Route>
                                    </Route>
                                </Route>
                            </Route>
                            <Route
                                path="/"
                                element={
                                    <MapProvider>
                                        <Home />
                                    </MapProvider>
                                }
                            />
                            <Route
                                path="/terms-and-conditions"
                                element={<TermAndConditions />}
                            />
                            <Route
                                path="/privacy-policy"
                                element={<PrivacyPolicy />}
                            />
                            <Route path="/drive" element={<Drive />} />
                            <Route path="/ride" element={<Ride />} />
                            <Route path="/support" element={<Support />} />
                            <Route path="/company" element={<Company />} />
                            <Route path="/faq" element={<Faq />} />
                            <Route
                                path={`${routes.paymentSuccess}/*`}
                                element={<PaymentSuccess />}
                            />
                            <Route
                                path={`${routes.paymentFailed}/*`}
                                element={<PaymentFailed />}
                            />
                            <Route path="/safety" element={<Safety />} />
                            {/* A shared trip, read by somebody with no account.
                                Declared out here with the marketing pages and
                                deliberately NOT inside PrivateRoute or the
                                dashboard shell: the whole point is that a
                                recipient can open it without signing in, and
                                wrapping it in the guard would redirect exactly
                                the person it was sent to. The token is opaque
                                rather than the ride id, so the path cannot be
                                guessed by counting. */}
                            <Route path="/t/:token" element={<SharedTripPage />} />
                            <Route
                                path="/sign-up"
                                element={<SignUp />}
                            />
                            <Route
                                path="/login"
                                element={<Login />}
                            />
                            <Route
                                path="/verify-email"
                                element={<VerifyEmail />}
                            />
                            {/* Behind PrivateRoute, unlike /verify-email: both
                                phone endpoints need a session, so an
                                unauthenticated visitor belongs at the sign-in
                                screen with this location remembered, not on a
                                page whose every request would 401. */}
                            <Route element={<PrivateRoute />}>
                                <Route
                                    path="/verify-phone"
                                    element={<VerifyPhone />}
                                />
                            </Route>
                            <Route
                                path="/get-started"
                                element={<GetStarted />}
                            />
                            <Route
                                path="/create-new-password"
                                element={<ForgotPassword />}
                            />
                            <Route
                                path="/reset-password"
                                element={<ResetPasword />}
                            />
                            {/* Last, because React Router matches "*" against
                                anything and a route declared after it would
                                never be reached.

                                Without a catch-all the router matched nothing
                                and rendered nothing, so a mistyped address gave
                                a completely blank document: the same symptom as
                                a route that crashed, which is what made the
                                blank /company page hard to tell apart from a
                                URL that simply does not exist. /terms is the
                                everyday case, the route being
                                /terms-and-conditions. */}
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </ScrollToTop>
                </Suspense>
                </RouteErrorBoundary>
            </Router>
        </>
    );
};

export default MajorRoutes;
